/**
 * ComboCounter 컴포넌트
 * 레트로 게임 스타일의 콤보 카운터
 * - 연속 정답 시 콤보 증가
 * - 깜빡이는 애니메이션
 * - 콤보 달성 시 확대 효과
 */

import React, { useEffect, useState } from 'react';

/**
 * ComboCounter Props
 */
interface ComboCounterProps {
  /**
   * 현재 콤보 수
   */
  combo: number;

  /**
   * 콤보 배율 (x1.5, x2, x3 등)
   */
  multiplier?: number;
}

/**
 * ComboCounter 컴포넌트
 * 콤보 카운터 및 배율 표시
 */
export const ComboCounter: React.FC<ComboCounterProps> = ({
  combo,
  multiplier = 1,
}) => {
  const [isNewCombo, setIsNewCombo] = useState(false);
  const [prevCombo, setPrevCombo] = useState(combo);

  /**
   * 콤보 증가 시 애니메이션 트리거
   */
  useEffect(() => {
    if (combo > prevCombo && combo > 0) {
      setIsNewCombo(true);
      const timer = setTimeout(() => setIsNewCombo(false), 500);
      return () => clearTimeout(timer);
    }
    setPrevCombo(combo);
  }, [combo]);

  // 콤보가 없으면 표시하지 않음
  if (combo === 0) return null;

  // 콤보 단계별 색상
  const getComboColor = () => {
    if (combo >= 10) return 'text-[#9bbc0f]'; // 10콤보 이상: 게임보이 그린
    if (combo >= 5) return 'text-white'; // 5콤보 이상: 흰색
    if (combo >= 3) return 'text-gray-300'; // 3콤보 이상: 밝은 회색
    return 'text-gray-400'; // 기본: 회색
  };

  return (
    <div className="font-pixel text-center">
      {/* COMBO 라벨 */}
      <div className="text-[10px] text-gray-500 mb-0.5">COMBO</div>

      {/* 콤보 카운터 */}
      <div
        className={`
          ${getComboColor()}
          text-xl md:text-3xl
          font-bold
          transition-all
          ${isNewCombo ? 'scale-125 animate-pulse' : 'scale-100'}
          ${combo >= 10 ? 'animate-blink' : ''}
        `}
        style={{
          textShadow:
            combo >= 10
              ? '0 0 10px #9bbc0f, 1px 1px 0 #0f380f'
              : combo >= 5
              ? '1px 1px 0 #000'
              : '1px 1px 0 #000',
        }}
      >
        {combo}
      </div>

      {/* 배율 표시 */}
      {multiplier > 1 && (
        <div
          className="mt-0.5 text-[#9bbc0f] text-xs md:text-sm"
          style={{
            textShadow: '0 0 8px #9bbc0f',
          }}
        >
          ×{multiplier.toFixed(1)}
        </div>
      )}
    </div>
  );
};
